import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Star, Calendar } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import ItemCard from "@/components/ItemCard";
import api from "@/lib/api";

export default function Profile() {
  const { userId } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/users/${userId}`)
      .then((res) => setProfile(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="animate-pulse space-y-6">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full bg-stone-100" />
            <div className="space-y-2"><div className="h-5 bg-stone-100 rounded w-40" /><div className="h-3 bg-stone-100 rounded w-24" /></div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-20 text-center">
        <p className="text-stone-500">User not found</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="profile-page">
      {/* Profile Header */}
      <div className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-8 mb-8">
        <div className="flex items-start gap-5">
          <Avatar className="w-20 h-20">
            <AvatarFallback className="bg-emerald-100 text-emerald-700 text-2xl font-heading font-semibold">
              {profile.name?.[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div>
            <h1 className="font-heading font-bold text-2xl text-stone-900" data-testid="profile-name">{profile.name}</h1>
            <div className="flex items-center gap-3 mt-1 text-sm text-stone-500 flex-wrap">
              {profile.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{profile.location}</span>}
              {profile.avg_rating > 0 && (
                <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5 fill-orange-400 text-orange-400" />{profile.avg_rating} ({profile.review_count} reviews)</span>
              )}
              <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />Joined {new Date(profile.created_at).toLocaleDateString("en-US", { month: "long", year: "numeric" })}</span>
            </div>
            {profile.bio && <p className="mt-3 text-sm text-stone-600 leading-relaxed max-w-lg">{profile.bio}</p>}
          </div>
        </div>
      </div>

      {/* Listings */}
      {profile.items?.length > 0 && (
        <div className="mb-10">
          <h2 className="font-heading font-semibold text-lg text-stone-900 mb-4" data-testid="profile-listings-title">
            {profile.name}'s Listings ({profile.items.length})
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {profile.items.map((item) => (
              <ItemCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      )}

      {/* Reviews */}
      {profile.reviews?.length > 0 && (
        <div>
          <h2 className="font-heading font-semibold text-lg text-stone-900 mb-4" data-testid="profile-reviews-title">
            Reviews ({profile.reviews.length})
          </h2>
          <div className="space-y-4">
            {profile.reviews.map((review) => (
              <div key={review.id} className="bg-white rounded-xl border border-stone-200 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="bg-stone-100 text-xs">{review.reviewer?.name?.[0]?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm font-medium text-stone-900">{review.reviewer?.name}</p>
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-3 h-3 ${i < review.user_rating ? "fill-orange-400 text-orange-400" : "text-stone-200"}`} />
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-sm text-stone-600">{review.comment}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
